<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';


import {computed, onBeforeUnmount, provide, ref, unref, watch} from 'vue'
import type {MaybeRef} from 'vue'
import {
  useFloating,
  autoUpdate,
  offset,
  shift,
  Placement,
  flip, size,
} from '@floating-ui/vue'
import {useEventListener} from '@vueuse/core'
import {CypressUniqueNameType} from '@toolify/client/cypress/types/CypressUniqueNameType'
import {useDropdown} from '@toolify/client/src/modules/modals/composables/useDropdown'
import {useOverlay} from '@toolify/client/src/modules/modals/composables/useOverlay'
import {onMounted} from 'vue'
import {useKeyboardShortcuts} from '../../keyboard/composables/useKeyboardShortcuts'
import {KeyboardSpecialKeyTypes} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardSpecialKeyTypes'
import {
  dropdownAvailableSpaceKey,
  type DropdownAvailableSpace,
} from '../composables/useDropdownAvailableSpace'
import {applyNaturalFloatingSize} from '../functions/applyNaturalFloatingSize'
import {createReferenceAnchoredSizeMiddleware} from '../functions/createReferenceAnchoredSizeMiddleware'

const VIEWPORT_PADDING = 16
const SELF_SIZED_CONTENT_SELECTOR = '[data-dropdown-self-sized]'

interface Props {
  isTransparent?: boolean
  placement?: Placement
  fallbackPlacements?: Placement[]
  isOpen?: boolean
  matchReferenceSize?: boolean
  minWidth?: number
  offsetMainAxis?: number
  offsetCrossAxis?: number
  shouldCaptureKeyboard?: boolean
  stackReferenceElement?: MaybeRef<HTMLElement | null>
  constrainToAvailableSpace?: boolean
  disableFlip?: boolean
  canCoverReference?: boolean
}

interface Emits {
  (e: 'hide'): void
}

const props = withDefaults(defineProps<Props>(), {
  isTransparent: false,
  placement: 'bottom',
  fallbackPlacements: undefined,
  isOpen: null,
  matchReferenceSize: false,
  minWidth: 0,
  offsetMainAxis: 5,
  offsetCrossAxis: 0,
  shouldCaptureKeyboard: true,
  constrainToAvailableSpace: true,
  disableFlip: false,
  canCoverReference: false,
})
const emit = defineEmits<Emits>()

const dropdownRef = _ref<HTMLElement>(null)
const referenceRef = _ref<HTMLElement>(null)

let isOpen = _ref<boolean>(props.isOpen)

watch(() => props.isOpen, (value) => {
  isOpen.value = value
})

const availableHeight = ref<number | null>(null)
const availableWidth = ref<number | null>(null)
const isSelfSizedContent = ref(false)

function detectSelfSizedContent() {
  isSelfSizedContent.value = !!dropdownRef.value?.querySelector(SELF_SIZED_CONTENT_SELECTOR)
}

const mayCoverReference = computed(() => isSelfSizedContent.value && props.canCoverReference)

const availableSpace = computed<DropdownAvailableSpace>(() => ({
  availableHeight: availableHeight.value,
  availableWidth: availableWidth.value,
}))

provide(dropdownAvailableSpaceKey, availableSpace)

function matchReferenceSizeFactory() {
  return size({
    apply({rects, elements}) {
      Object.assign(elements.floating.style, {
        width: `${Math.max(rects.reference.width, props.minWidth)}px`,
      })
    },
  })
}

const matchReferenceSizeMiddleware = ref<ReturnType<typeof size> | null>(null)

const floatingMiddleware = computed(() => {
  const overflowOptions = {
    padding: VIEWPORT_PADDING,
    // Teleported into #teleported — collide against the visible viewport.
    rootBoundary: 'viewport' as const,
  }

  const list = [
    offset({
      mainAxis: props.offsetMainAxis,
      crossAxis: props.offsetCrossAxis,
    }),
  ]

  // maxHeight from a previous frame would make flip think content still fits,
  // and on the first open there is no rect to read at all. Patch rects to the
  // size the panel will really take; size() then resets to real DOM rects.
  // Only a panel allowed to cover the trigger escapes clamping, so only that
  // one can trust its live rect.
  if (props.constrainToAvailableSpace && !mayCoverReference.value) {
    list.push(applyNaturalFloatingSize(isSelfSizedContent.value))
  }

  if (!props.disableFlip) {
    list.push(flip({
      ...overflowOptions,
      // Prefer opposite side with the same alignment (right-start → left-start).
      flipAlignment: false,
      crossAxis: false,
      // When neither side fits natural size, pick the one with more room.
      fallbackStrategy: 'bestFit',
      ...(props.fallbackPlacements?.length
        ? {fallbackPlacements: props.fallbackPlacements}
        : {}),
    }))
  }

  // Alignment axis only (X for top/bottom, Y for left/right). Shifting on the
  // placement axis slides the panel over the trigger, so it stays opt-in.
  list.push(shift({
    ...overflowOptions,
    mainAxis: true,
    crossAxis: mayCoverReference.value,
  }))

  if (props.constrainToAvailableSpace) {
    // Cap to [trigger, offset, availableSpace, viewportPadding, viewport].
    list.push(createReferenceAnchoredSizeMiddleware({
      padding: VIEWPORT_PADDING,
      offsetMainAxis: props.offsetMainAxis,
      isSelfSized: mayCoverReference.value,
      onApply: ({availableHeight: nextH, availableWidth: nextW}) => {
        availableHeight.value = nextH
        availableWidth.value = nextW
      },
    }))
  }

  if (matchReferenceSizeMiddleware.value) {
    list.push(matchReferenceSizeMiddleware.value)
  }

  return list
})

onMounted(() => {
  if (props.matchReferenceSize) {
    matchReferenceSizeMiddleware.value = matchReferenceSizeFactory()
  }
})

const {floatingStyles, update, isPositioned} = useFloating((referenceRef), (dropdownRef), {
  placement: computed(() => props.placement),
  middleware: floatingMiddleware,
  whileElementsMounted: autoUpdate,
  open: (isOpen),
})

watch(isPositioned, (value) => {
  if (!value) {
    availableHeight.value = null
    availableWidth.value = null
  }
})

watch(
  () => [props.placement, props.fallbackPlacements, props.offsetMainAxis, props.offsetCrossAxis, props.constrainToAvailableSpace, props.disableFlip],
  () => {
    void update()
  },
)

watch(isSelfSizedContent, () => {
  void update()
})

const {
  addDropdown,
  removeDropdown,
} = useDropdown()

const dropdownId = Symbol()

watch(isPositioned, (value) => {
  if(value) {
    addDropdown(dropdownId)
  } else {
    removeDropdown(dropdownId)
  }
}, {immediate: true})

onBeforeUnmount(() => {
  removeDropdown(dropdownId)
})

const overlayStackReference = computed(() => {
  const boundary = props.stackReferenceElement !== undefined ? unref(props.stackReferenceElement) : null
  return boundary ?? unref(referenceRef.value)
})

const {zIndex} = useOverlay({
  element: (dropdownRef),
  referenceElement: overlayStackReference,
  isOpen: (isOpen),
  onClose: onClose,
})

const onOpenEvent = () => {
  if(typeof props.isOpen === 'boolean') {
    return
  }
  isOpen.value = true
}

useEventListener(window, 'resize', () => {
  void update()
})

let wasOpenAtInteractionStart = false

const onReferencePointerDownCapture = () => {
  wasOpenAtInteractionStart = typeof props.isOpen === 'boolean' ? !!props.isOpen : isOpen.value
}

const onReferenceClickCapture = (event: MouseEvent) => {
  if (typeof props.isOpen === 'boolean' && props.isOpen && wasOpenAtInteractionStart) {
    onClose()
    event.stopImmediatePropagation()
  }
}

const onReferenceClickBubble = () => {
  if (typeof props.isOpen === 'boolean') {
    return
  }
  if (isOpen.value) {
    onClose()
  } else {
    isOpen.value = true
  }
}

watch((referenceRef), (value, oldValue) => {
  oldValue?.removeEventListener('pointerdown', onReferencePointerDownCapture, true)
  oldValue?.removeEventListener('click', onReferenceClickCapture, true)
  oldValue?.removeEventListener('click', onReferenceClickBubble, false)
  value?.addEventListener('pointerdown', onReferencePointerDownCapture, true)
  value?.addEventListener('click', onReferenceClickCapture, true)
  value?.addEventListener('click', onReferenceClickBubble, false)
})

onBeforeUnmount(() => {
  if(referenceRef.value) {
    referenceRef.value.removeEventListener('pointerdown', onReferencePointerDownCapture, true)
    referenceRef.value.removeEventListener('click', onReferenceClickCapture, true)
    referenceRef.value.removeEventListener('click', onReferenceClickBubble, false)
  }
})

let contentMutationObserver: MutationObserver | null = null
let remeasureFrameId: number | null = null

function scheduleContentRemeasure() {
  if (!isOpen.value || props.disableFlip) {
    return
  }
  if (remeasureFrameId !== null) {
    return
  }
  remeasureFrameId = requestAnimationFrame(() => {
    remeasureFrameId = null
    if (!isOpen.value) {
      return
    }
    detectSelfSizedContent()
    // Content changed — re-run flip/size against natural size.
    void update()
  })
}

function disconnectContentObservers() {
  contentMutationObserver?.disconnect()
  contentMutationObserver = null
  if (remeasureFrameId !== null) {
    cancelAnimationFrame(remeasureFrameId)
    remeasureFrameId = null
  }
}

watch((dropdownRef), (element) => {
  disconnectContentObservers()
  if (!element) {
    isSelfSizedContent.value = false
    return
  }
  detectSelfSizedContent()
  // DOM content changes may not resize the clamped floating element.
  contentMutationObserver = new MutationObserver(() => {
    scheduleContentRemeasure()
  })
  contentMutationObserver.observe(element, {
    childList: true,
    subtree: true,
    characterData: true,
  })
})

onBeforeUnmount(() => {
  disconnectContentObservers()
})

useKeyboardShortcuts([
  [[KeyboardSpecialKeyTypes.Escape], (event?: KeyboardEvent) => {
    if(!event?.defaultPrevented) {
      onClose()
    }
  }],
  [[null], () => {
  }],
], () => {
  return props.shouldCaptureKeyboard && (!!isOpen.value || !!props.isOpen)
})


function onClose() {
  isOpen.value = false
  emit('hide')
}

defineExpose(({
  isOpen,
}))

const dropdownContentStyle = _computed(() => {
  const styles: Record<string, string | number>[] = [floatingStyles.value as Record<string, string | number>]
  if (zIndex.value) {
    styles.push({zIndex: zIndex.value})
  }
  if (availableHeight.value !== null) {
    styles.push({
      '--dropdown-available-height': `${availableHeight.value}px`,
      '--dropdown-available-width': `${availableWidth.value ?? 0}px`,
    })
  }
  return styles
})
</script>

<template>
  <div class="dropdown-wrapper" ref="referenceRef">
    <slot :isOpen="isOpen"/>
    <Teleport to="#teleported">
      <div
        class="dropdown-component-content"
        :class="{ 'constrain-to-available-space': props.constrainToAvailableSpace }"
        v-if="isOpen"
        ref="dropdownRef"
        :style="dropdownContentStyle"
        :data-cy="CypressUniqueNameType.DropdownComponentContent"
      >
        <div class="dropdown-component-content-inner">
          <slot
            name="dropdown"
            :isOpen="isOpen"
            :close="onClose"
            :availableHeight="availableHeight"
            :availableWidth="availableWidth"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
  @use '../../../styles/variables' as variables;
  .dropdown-component-content {
    background: map-get(variables.$colors, container);
    box-shadow: variables.$dropdownShadow;
    border-radius: variables.$dropdownRadius;
    overflow: hidden;
    box-sizing: border-box;

    &.constrain-to-available-space {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  }

  .dropdown-component-content-inner {
    display: flex;
    flex-direction: column;
    flex: 0 1 auto;
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
    box-sizing: border-box;
    width: 100%;
  }

  .dropdown-wrapper{
    width: 100%;
    height: 100%;
    display: grid;
    align-items: center;
  }
</style>
